import { useEffect, useRef, useState } from "react";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { toast } from "react-toastify";
import { usePanel } from "hooks/usePanel";
import { GenericLoading } from "components/ui/Loader";
import { OperationResponse } from "api/actor";
import {
  applyValidationErrorsToForm,
  createPersonFieldResolver,
  parseApiError,
} from "shared/utils/apiValidationErrors";
import { gestionnaireFormConfig } from "./gestionnaireFormConfig";
import { GestionnaireFormValues } from "./GestionnaireFormValues";
import { GestionnaireLayout } from "./GestionnaireLayout";

interface GestionnaireFormProps {
  onSuccess?: () => void;
  onSubmit: (values: GestionnaireFormValues) => Promise<OperationResponse | undefined>;
}

export const GestionnaireForm: React.FC<GestionnaireFormProps> = ({
  onSubmit,
  onSuccess,
}) => {
  const formikRef = useRef<FormikProps<GestionnaireFormValues>>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setPanelActions, closePanel } = usePanel();

  useEffect(() => {
    if (formikRef.current) {
      setPanelActions({
        closeLabel: "COMMON.CANCEL",
        submitLabel: "COMMON.VALIDATE",
        submitDisabled: !isFormValid,
        onSubmit: () => {
          formikRef.current?.submitForm();
        },
      });
    }
  }, [formikRef, setPanelActions, isFormValid]);

  const handleSubmit = async (
    values: GestionnaireFormValues,
    helpers: FormikHelpers<GestionnaireFormValues>
  ) => {
    setIsLoading(true);
    try {
      const response = await onSubmit(values);
      if (response?.isSuccess) {
        onSuccess?.();
        closePanel();
      } else if (response?.errorMessage) {
        toast.error(response.errorMessage);
      }
    } catch (error) {
      console.error('Error on submit gestionnaire:', error);
      const parsed = await parseApiError(error);
      const matched = applyValidationErrorsToForm({
        validationErrors: parsed.validationErrors,
        setFieldError: helpers.setFieldError,
        setFieldTouched: helpers.setFieldTouched,
        resolveField: createPersonFieldResolver(),
      });
      if (parsed.message || matched > 0) {
        toast.error(parsed.message || 'Veuillez corriger les champs en erreur');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      {isLoading && <GenericLoading loading isParentSized />}
      <Formik
        enableReinitialize
        initialValues={gestionnaireFormConfig.initialValues}
        validationSchema={gestionnaireFormConfig.validationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {({ isValid }) => {
          if (isValid !== isFormValid) {
            setIsFormValid(isValid);
          }
          return (
            <Form>
              <GestionnaireLayout />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
